import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";
import OutsideClickHandler from "react-outside-click-handler";

import { KEY_NUMBERS } from "../../constants/keyboardKeys";
import Text from "../Text";
import Tooltip from "../Tooltip";
import { ActionIcon, MessageBoxWrapper, StyledButton, StyledIcon, StyledSubHeading, ToggletipHeaderWrapper, ToggleTipWrapper } from "./style";

var ToggleTip = function ToggleTip(_ref) {
  var title = _ref.title,
      message = _ref.message,
      icon = _ref.icon,
      offset = _ref.offset,
      position = _ref.position,
      hideTooltip = _ref.hideTooltip,
      callToActionText = _ref.callToActionText,
      callToActionIcon = _ref.callToActionIcon,
      callToActionCallback = _ref.callToActionCallback,
      toggleCallback = _ref.toggleCallback;

  var _useState = useState(false),
      open = _useState[0],
      setOpen = _useState[1];

  var modalRef = useRef();
  var wrapperRef = useRef();
  var handleClick = function handleClick(type) {
    return function () {
      if (type === "TOGGLE") {
        if (toggleCallback) toggleCallback();
        return setOpen(function (prev) {
          return !prev;
        });
      }

      return setOpen(false);
    };
  };

  var focusIcon = function focusIcon() {
    if (wrapperRef.current) {
      var iconElement = wrapperRef.current.querySelector("#toggle-icon");
      iconElement.focus();
    }
  };

  var handleModalKeyDown = function handleModalKeyDown(e) {
    if (e.key === "Control") {
      setOpen(false);
      focusIcon();
    }
  };

  var handleIconKeyDown = function handleIconKeyDown(e) {
    var KEY_ENTER = KEY_NUMBERS.KEY_ENTER,
        KEY_SPACE = KEY_NUMBERS.KEY_SPACE;


    switch (e.which) {
      case KEY_ENTER:
        if (open && modalRef.current) {
          modalRef.current.focus();
          break;
        }
        setOpen(true);
        break;

      case KEY_SPACE:
        e.preventDefault();
        if (open && modalRef.current) {
          modalRef.current.focus();
          break;
        }
        setOpen(true);
        break;

      default:
        break;
    }
  };

  useEffect(function () {
    if (modalRef.current) {
      modalRef.current.focus();
    }
  }, [open]);

  var shouldRenderCTA = !!callToActionText && !!callToActionCallback;

  return React.createElement(
    OutsideClickHandler,
    { onOutsideClick: handleClick("CLOSE") },
    React.createElement(
      ToggleTipWrapper,
      {
        offset: offset,
        open: open,
        onKeyDown: handleIconKeyDown,
        ref: wrapperRef
      },
      hideTooltip ? React.createElement(ActionIcon, {
        icon: icon,
        color: open ? "primary" : "gray",
        onClick: handleClick("TOGGLE"),
        "aria-label": "toggle",
        id: "toggle-icon",
        role: "button",
        tabIndex: 0
      }) : React.createElement(
        Tooltip,
        { title: "Mais informa\xE7\xF5es", placement: "top" },
        React.createElement(ActionIcon, {
          icon: icon,
          color: open ? "primary" : "gray",
          onClick: handleClick("TOGGLE"),
          "aria-label": "toggle",
          id: "toggle-icon",
          role: "button",
          tabIndex: 0
        })
      ),
      open && React.createElement(
        MessageBoxWrapper,
        {
          "aria-label": "Caixa texto explicativo",
          ref: modalRef,
          tabIndex: 0,
          onKeyDown: handleModalKeyDown,
          position: position
        },
        React.createElement(
          ToggletipHeaderWrapper,
          null,
          React.createElement(
            "div",
            null,
            React.createElement(
              StyledSubHeading,
              { transform: "uppercase", size: "sm", tabIndex: 0 },
              title
            )
          ),
          React.createElement(StyledIcon, {
            icon: "fa-times",
            color: "gray",
            onClick: handleClick("CLOSE"),
            "aria-label": "close"
          })
        ),
        React.createElement(
          Text,
          { noMargin: true, "aria-label": "Texto explicativo", tabIndex: 0 },
          message
        ),
        shouldRenderCTA && React.createElement(
          StyledButton,
          {
            icon: callToActionIcon && "fa-question-circle-o",
            positionIcon: "left",
            color: "primary",
            plain: true,
            block: true,
            onClick: callToActionCallback
          },
          callToActionText
        )
      )
    )
  );
};

ToggleTip.propTypes = {
  /** Icon that triggers the message box.
   * Only icons that exist in Font Awesome version 4.7.0 are supported. */
  icon: PropTypes.string.isRequired,
  /** Title of the message box. */
  title: PropTypes.string.isRequired,
  /** String or node that appears inside the message box body. */
  message: PropTypes.oneOf([PropTypes.string, PropTypes.node]).isRequired,
  /** Toggletip position relative to clicker icon. */
  position: PropTypes.oneOf(["right", "left"]),
  /** Clickable area offset. */
  offset: PropTypes.object,
  /** Boolean flag to determine if the tooltip should be hidden. */
  hideTooltip: PropTypes.bool,
  /** Optional string to display as the call-to-action (CTA) at the bottom of the tooltip body.
   * Required for rendering the CTA. */
  callToActionText: PropTypes.string,
  /** Optional external link used for the CTA (required for CTA rendering. */
  callToActionCallback: PropTypes.func,
  /** Optional flag to display a question mark icon to the left of the button text.
   * Not required for CTA rendering. */
  callToActionIcon: PropTypes.bool,
  /** Optional callback which is called when the toggletip is clicked. */
  toggleCallback: PropTypes.func
};

ToggleTip.defaultProps = {
  offset: undefined,
  position: "right",
  hideTooltip: false,
  callToActionIcon: false,
  callToActionText: undefined,
  callToActionCallback: undefined,
  toggleCallback: undefined
};

export default ToggleTip;